import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack, Typography, Chip, Divider, Paper, Tooltip } from '@mui/material';
import { t } from 'i18next';
import { baseUrl } from '../../../../util/constant';
import Logo from '../../../../assets/images/logo/vms-pro-logo.svg';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BuildIcon from '@mui/icons-material/Build';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

const VehiclePrintPreview = ({ open, onClose, vehicle }) => {
  if (!vehicle) return null;

  const booking = vehicle.bookingDTO || {};
  const client = vehicle.client || {};
  const exporter = client.exporter || {};
  const pictures = vehicle.warehousePictures || [];
  const description = [vehicle?.vehicleType?.name, vehicle?.year, vehicle?.make?.name, vehicle?.model?.name, vehicle?.color?.name]
    .filter(Boolean)
    .join(' ');

  const imgSrc = (f) => `${baseUrl}${f?.filePath || ''}`;

  const handlePrint = () => window.print();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Box component="img" src={Logo} alt="VMS PRO" sx={{ height: 28, width: 'auto' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0B3C5D' }}>{t('Vehicle Preview')} #{vehicle.id}</Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Generated At')}: {new Date().toLocaleString()}</Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#FAFBFC' }}>
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .print-area, .print-area * { visibility: visible; }
            .print-area { position: absolute; inset: 0; padding: 0 !important; }
            .no-print { display: none !important; }
            .table-header { background: #e5e7eb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .section-title { background:#0B3C5D !important; color:#fff !important; padding:4px 8px; border-radius:4px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>
        <Box className="print-area" sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, pb:1, borderBottom:'2px solid #0B3C5D' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
              <Box component="img" src={Logo} alt="VMS PRO" sx={{ height: 40, width:'auto' }} />
              <Typography variant="caption" sx={{ color:'text.secondary' }}>{t('Vehicle Document')}</Typography>
            </Box>
            <Typography variant="caption" sx={{ fontStyle:'italic', color:'text.secondary' }}>Powered by Pinetechs.com</Typography>
          </Box>

          {/* Top summary chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip size="small" color="default" label={`${t('Status')}: ${vehicle?.status?.name || '-'}`} />
            <Chip size="small" color="info" label={`${t('VIN')}: ${vehicle?.vin || '-'}`} />
            {booking?.bookingNumber && (<Chip size="small" color="primary" label={`${t('Booking Number')}: ${booking.bookingNumber}`} />)}
            {vehicle?.price ? <Chip size="small" color="warning" label={`${t('Price')}: ${vehicle.price}`} /> : null}
          </Stack>

          {/* Hero image */}
          {pictures.length > 0 && (
            <Paper elevation={0} sx={{ p:1, mb:2, border:'1px solid #e9ecef', borderRadius:2, bgcolor:'#fff' }}>
              <Box component="img" src={imgSrc(pictures[0])} alt={pictures[0]?.originalFileName || ''} sx={{ width:'100%', height: 260, objectFit:'cover', borderRadius: 2 }} />
            </Paper>
          )}

          {/* Info cards */}
          <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'repeat(3, 1fr)' }, gap:2, mb:2 }}>
            <Paper elevation={0} sx={{ p:2, border:'1px solid #e9ecef', borderRadius:2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0B3C5D', mb: 1 }}>{t('Vehicle')}</Typography>
              <KV k={t('Description')} v={description || '-'} />
              <KV k={t('Type')} v={vehicle?.vehicleType?.name} />
              <KV k={t('Year')} v={vehicle?.year} />
              <KV k={t('Make')} v={vehicle?.make?.name} />
              <KV k={t('Model')} v={vehicle?.model?.name} />
              <KV k={t('Color')} v={vehicle?.color?.name} />
              <KV k={t('Fuel Type')} v={vehicle?.fuelType?.name} />
              <KV k={t('Destination')} v={vehicle?.destination?.name} />
              <KV k={t('Weight')} v={vehicle?.weight} />
              <KV k={t('Warehouse Arrival')} v={vehicle?.warehouseArrivalDate} />
              <KV k={t('ETA Warehouse Arrival')} v={vehicle?.etaWarehouseArrivalDate} />
            </Paper>

            <Paper elevation={0} sx={{ p:2, border:'1px solid #e9ecef', borderRadius:2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0B3C5D', mb: 1 }}>{t('Client')}</Typography>
              <KV k={t('Name')} v={client?.name} />
              <KV k={t('Company')} v={client?.company} />
              <Divider sx={{ my:1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0B3C5D', mb: 1 }}>{t('Exporter')}</Typography>
              <KV k={t('Name')} v={exporter?.name} />
              <KV k={t('Export #')} v={exporter?.exportNumber} />
              <KV k={t('Email')} v={exporter?.email} />
              <KV k={t('Phone')} v={exporter?.phoneNumber} />
            </Paper>

            <Paper elevation={0} sx={{ p:2, border:'1px solid #e9ecef', borderRadius:2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0B3C5D', mb: 1 }}>{t('Booking')}</Typography>
              <KV k={t('Vessel')} v={booking?.vesselName} />
              <KV k={t('Voyage')} v={booking?.voyageNumber} />
              <KV k={t('POL')} v={booking?.portLoading} />
              <KV k={t('Pickup Terminal')} v={booking?.pickupTerminal?.name} />
              <KV k={t('Return Terminal')} v={booking?.returnTerminal} />
              <KV k={t('Est. Sailing')} v={booking?.estimatedSailingDate} />
              <KV k={t('Est. Arrival')} v={booking?.estimatedArrivalDate} />
            </Paper>
          </Box>

          {/* Flags */}
          <Paper elevation={0} sx={{ p:2, border:'1px solid #e9ecef', borderRadius:2, mb:2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0B3C5D', mb: 1 }}>{t('Flags')}</Typography>
            <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'repeat(2,1fr)', sm:'repeat(3,1fr)', md:'repeat(5,1fr)' }, gap:1.25 }}>
              <Flag label={t('Key')} enabled={!!vehicle?.key} icon={<VpnKeyIcon />} />
              <Flag label={t('Title')} enabled={!!vehicle?.title} icon={<DescriptionOutlinedIcon />} />
              <Flag label={t('Has Catalyst')} enabled={!!vehicle?.hasCatalyst} icon={<ScienceOutlinedIcon />} />
              <Flag label={t('Remove Engine')} enabled={!!vehicle?.removeEngine} icon={<BuildIcon />} />
              <Flag label={t('Parts With Vehicle')} enabled={!!vehicle?.partsWithCar} icon={<Inventory2OutlinedIcon />} />
            </Box>
          </Paper>

          {/* Thumbnails */}
          <Typography variant="h6" className="section-title" sx={{ mb: 2, display: 'inline-block' }}>{t('Pictures')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1 }}>
            {pictures.length > 0 ? (
              pictures.map((f, idx) => (
                <Box key={f.id} sx={{ p: 1, bgcolor: '#fff', border: '1px solid #e9ecef', borderRadius: 1, opacity: idx === 0 ? 0.85 : 1 }}>
                  <Box component="img" src={imgSrc(f)} alt={f.originalFileName || ''} sx={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 1 }} />
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">{t('No pictures')}</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className="no-print" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">VIN: {vehicle?.vin || '-'}</Typography>
        <Box>
          <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>{t('Close')}</Button>
          <Button onClick={handlePrint} variant="contained">{t('Print')}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const KV = ({ k, v }) => (
  <Box sx={{ display:'flex', justifyContent:'space-between', gap: 1, py: .25 }}>
    <Typography variant="caption" sx={{ color:'text.secondary' }}>{k}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color:'#0B3C5D' }}>{v ?? '-'}</Typography>
  </Box>
);

KV.propTypes = { k: PropTypes.node, v: PropTypes.node };

VehiclePrintPreview.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicle: PropTypes.object,
};

export default VehiclePrintPreview;

const Flag = ({ label, enabled, icon }) => (
  <Tooltip title={`${label}: ${enabled ? t('True') : t('False')}`} arrow>
    <Box sx={{ display:'flex', alignItems:'center', gap: 1.25 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: enabled ? 'success.light' : 'grey.200',
          color: enabled ? 'success.main' : 'text.disabled',
          border: '1px solid',
          borderColor: enabled ? 'success.main' : 'grey.300',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display:'flex', alignItems:'center', gap: .5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>{label}</Typography>
        {enabled ? (
          <CheckCircleRoundedIcon fontSize="small" color="success" />
        ) : (
          <CancelRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        )}
      </Box>
    </Box>
  </Tooltip>
);

Flag.propTypes = { label: PropTypes.node, enabled: PropTypes.bool, icon: PropTypes.node };
